import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import HomeRealEstateAgentFormImg from '../assets/images/home-real-estate-agent-form-img.png'
import { CONTACT_AN_AGENT, ASSETS_URL } from "../api/constants";
import Swal from 'sweetalert2';
import { useParams } from "react-router-dom";
import useCategories from "../hooks/useCategories";

const HomeRealEstateAgentForm = ({ property, section }) => {
  const { id } = useParams();
  const { categories } = useCategories();

  // Default values
  const title = section?.title || "Talk to a Real Estate Agent";
  const subtitle = section?.subtitle || "You'll be connected with an expert local agent—there's no pressure or obligation.";
  
  // Build image URL from API response or use default
  const formImage = section?.image 
    ? `${ASSETS_URL}${section.image}`
    : HomeRealEstateAgentFormImg;

  const user = JSON.parse(localStorage.getItem("user"));
  const token = localStorage.getItem("token");
  let user_id = null;
  if (user?.role === "client") {
    user_id = user.id;
  }

  const isLoggedIn = !!(user && token);

  const initialFormData = {
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    property_type: "",
    intent: "buy",
    message: "",
    wants_financing_info: false,
    inquiry_type: id ? 'property' : 'general',
    property_id: id || null,
    agent_id: property?.agent_id || '',
    user_id: user_id,
  };

  const [formData, setFormData] = useState(initialFormData);

  // Prefill user details when logged in
  useEffect(() => {
    if (!isLoggedIn || !user) return;

    setFormData(prev => ({
      ...prev,
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
      phone: user.phone_number || '',
    }));
  }, [isLoggedIn]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const res = await fetch(CONTACT_AN_AGENT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(formData),
      });

      const data = await res.json();

      if (data.status) {
        e.target.reset();
        setFormData(initialFormData);
        Swal.fire({
          title: 'Success!',
          text: data.message,
          icon: 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: '#0abab5'
        });
      } else {
        Swal.fire({
          title: 'Error',
          text: data.message || "Something went wrong",
          icon: 'error'
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      Swal.fire({
        title: 'Error',
        text: "Failed to submit inquiry",
        icon: 'error'
      });
    }
  };

  return (
    <div className="home-real-estate-agent common-padding">
      <Container>
        <Row>
          {/* Left Image */}
          <Col lg={6}>
            <img
              src={formImage}
              alt="Living Room"
              className="h-100 w-100 d-none d-lg-block"
            />
          </Col>

          {/* Right Form Section */}
          <Col lg={6}>
            <div className="form-wrap">
              <div className="common-header">
                <h2>{title}</h2>
                <p>{subtitle}</p>
              </div>

              <Form onSubmit={handleSubmit}>

                {/* Only show personal fields when NOT logged in */}
                {!isLoggedIn && (
                  <Row className="mb-4">
                    <Col md={6}>
                      <Form.Label>First Name <span>*</span></Form.Label>
                      <Form.Control
                        name="first_name"
                        type="text"
                        placeholder="Ex. John"
                        value={formData.first_name}
                        onChange={handleChange}
                        required
                      />
                    </Col>
                    <Col md={6}>
                      <Form.Label>Last Name <span>*</span></Form.Label>
                      <Form.Control
                        name="last_name"
                        type="text"
                        placeholder="Ex. Doe"
                        value={formData.last_name}
                        onChange={handleChange}
                        required
                      />
                    </Col>
                  </Row>
                )}

                {!isLoggedIn && (
                  <Row className="mb-4">
                    <Col md={6}>
                      <Form.Label>Email <span>*</span></Form.Label>
                      <Form.Control
                        name="email"
                        type="email"
                        placeholder="Ex. xyz@domain.com"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </Col>
                    <Col md={6}>
                      <Form.Label>Phone <span>*</span></Form.Label>
                      <Form.Control
                        name="phone"
                        type="text"
                        placeholder="Ex. +19876543210"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                      />
                    </Col>
                  </Row>
                )}

                {/* Always shown */}
                <Row className="mb-4">
                  <Col md={6}>
                    <Form.Label>Property Type <span>*</span></Form.Label>
                    <Form.Select
                      name="property_type"
                      value={formData.property_type}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Type</option>
                      {categories?.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={6}>
                    <Form.Label>I Want <span>*</span></Form.Label>
                    <Form.Select
                      name="intent"
                      value={formData.intent}
                      onChange={handleChange}
                    >
                      <option value="buy">Buy</option>
                      <option value="sell">Sell</option>
                    </Form.Select>
                  </Col>
                </Row>

                <Form.Group className="mb-4">
                  <Form.Label>What can we help you with? <span>*</span></Form.Label>
                  <Form.Control
                    name="message"
                    as="textarea"
                    rows={3}
                    placeholder="Write here..."
                    value={formData.message}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>

                <Form.Check
                  type="checkbox"
                  name="wants_financing_info"
                  id="financeInfo"
                  label="I want financing information."
                  className='custom-checkbox mb-4'
                  checked={formData.wants_financing_info}
                  onChange={handleChange}
                />

                <Button type="submit" className="custom-bttn style-1">
                  Submit
                </Button>

                <p className="note" dangerouslySetInnerHTML={{ 
                  __html: section?.description || "By submitting this form, I agree to receive calls and SMS messages from Redfin for the purpose of updates and promotions. Messages may be sent on a recurring basis and frequency will vary. Message and data rates may apply. Consent to receive SMS messages is not required as a condition for purchasing any goods or services. To unsubscribe from SMS messages, reply \"STOP\" at any time. For assistance, reply \"HELP\" or visit redfin.com. By proceeding, you confirm that you are creating a Redfin account and have read and agree to our Privacy Policy and Terms of Use."
                }} />
              </Form>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default HomeRealEstateAgentForm;